import { useRef, type ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { MarkdownView } from './MarkdownView';
import './markdown-editor.css';

/**
 * Editor Markdown con barra strumenti e anteprima affiancata.
 *
 * Markdown e non un WYSIWYG che produce HTML: il testo salvato resta leggibile
 * e ispezionabile, l'hash copre esattamente ciò che una persona legge, e il
 * rendering ammette solo un insieme chiuso di elementi (vedi `MarkdownView`).
 *
 * Le icone sono SVG inline: §17 vieta font di icone o CDN esterni, e la CSP li
 * bloccherebbe comunque.
 */

type Action =
  | { kind: 'wrap'; before: string; after: string }
  | { kind: 'linePrefix'; prefix: string };

type ToolbarButton = {
  readonly id: string;
  readonly action: Action;
  readonly icon: ReactNode;
};

/* Icone: tratto uniforme, currentColor, nessuna dipendenza. */
const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const BUTTONS: readonly ToolbarButton[] = [
  {
    id: 'heading',
    action: { kind: 'linePrefix', prefix: '## ' },
    icon: (
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
        <path d="M4 4v12M12 4v12M4 10h8" {...stroke} />
        <path d="M15 8h2v8" {...stroke} />
      </svg>
    ),
  },
  {
    id: 'bold',
    action: { kind: 'wrap', before: '**', after: '**' },
    icon: (
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
        <path d="M6 4h5a3 3 0 0 1 0 6H6zM6 10h6a3 3 0 0 1 0 6H6z" {...stroke} />
      </svg>
    ),
  },
  {
    id: 'italic',
    action: { kind: 'wrap', before: '_', after: '_' },
    icon: (
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
        <path d="M12 4H8M12 16H8M13 4l-4 12" {...stroke} />
      </svg>
    ),
  },
  {
    id: 'bulletList',
    action: { kind: 'linePrefix', prefix: '- ' },
    icon: (
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
        <path d="M8 5h9M8 10h9M8 15h9" {...stroke} />
        <circle cx="4" cy="5" r="1.2" fill="currentColor" />
        <circle cx="4" cy="10" r="1.2" fill="currentColor" />
        <circle cx="4" cy="15" r="1.2" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'numberedList',
    action: { kind: 'linePrefix', prefix: '1. ' },
    icon: (
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
        <path d="M8 5h9M8 10h9M8 15h9" {...stroke} />
        <path d="M3 3.5h1V7M3 12h2l-2 3h2" {...stroke} />
      </svg>
    ),
  },
  {
    id: 'link',
    action: { kind: 'wrap', before: '[', after: '](https://)' },
    icon: (
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
        <path d="M8 12a3 3 0 0 0 4 0l3-3a3 3 0 0 0-4-4l-1 1" {...stroke} />
        <path d="M12 8a3 3 0 0 0-4 0l-3 3a3 3 0 0 0 4 4l1-1" {...stroke} />
      </svg>
    ),
  },
];

export function MarkdownEditor({
  id,
  value,
  onChange,
  rows = 18,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  rows?: number;
}) {
  const { t } = useI18n();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function apply(action: Action): void {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end);

    let next: string;
    let cursor: number;

    if (action.kind === 'wrap') {
      next = `${value.slice(0, start)}${action.before}${selected}${action.after}${value.slice(end)}`;
      // Senza selezione il cursore va fra i delimitatori, così si scrive subito.
      cursor = selected.length > 0 ? end + action.before.length + action.after.length : start + action.before.length;
    } else {
      // Il prefisso si applica a tutte le righe toccate dalla selezione.
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = value.indexOf('\n', end) === -1 ? value.length : value.indexOf('\n', end);
      const block = value.slice(lineStart, lineEnd);
      const prefixed = block
        .split('\n')
        .map((line) => (line.startsWith(action.prefix) ? line : `${action.prefix}${line}`))
        .join('\n');

      next = `${value.slice(0, lineStart)}${prefixed}${value.slice(lineEnd)}`;
      cursor = lineStart + prefixed.length;
    }

    onChange(next);
    // Il focus torna nel testo: la barra non deve interrompere la scrittura.
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  }

  return (
    <div className="md-editor">
      <div className="md-editor__toolbar" role="toolbar" aria-label={t.markdown.toolbar}>
        {BUTTONS.map((button) => (
          <button
            key={button.id}
            type="button"
            className="md-editor__tool"
            // Pulsante con sola icona: senza etichetta accessibile sarebbe muto
            // per uno screen reader (§20.6).
            aria-label={t.markdown.actions[button.id as keyof typeof t.markdown.actions]}
            title={t.markdown.actions[button.id as keyof typeof t.markdown.actions]}
            onClick={() => apply(button.action)}
          >
            {button.icon}
          </button>
        ))}
        <span className="md-editor__hint">{t.markdown.hint}</span>
      </div>

      <div className="md-editor__panes">
        <textarea
          id={id}
          ref={textareaRef}
          className="acs-input md-editor__source"
          rows={rows}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          spellCheck
        />
        <div className="md-editor__preview" aria-live="polite">
          <span className="md-editor__preview-label">{t.markdown.preview}</span>
          <MarkdownView content={value} className="md-editor__rendered" />
        </div>
      </div>
    </div>
  );
}
